// Sdílené typy a formátování pro Sklad látek (Střihárna).

export type Movement = {
  id: number;
  type: 'subtract' | 'adjust' | 'spent';
  delta: number;
  result_qty: number;
  user_name: string;
  created_at: number;
  /** ID výrobní zakázky, na kterou se metráž spotřebovala (jinak prázdné). */
  order_ref?: string | null;
};

/** Jedna fyzická role látky na skladě (položka objednávky po příjmu). */
export type Piece = {
  id: number;
  order_id: number;
  serial_number: number;
  latka_id: string;
  latka_code: string;
  latka_name: string;
  latka_type: string;
  latka_image: string;
  unit: string;
  quantity: number;
  stock_qty: number;
  received: number;
  received_at: number | null;
  received_by: string | null;
  spent_at: number | null;
  created_at: number;
  /** 0 = role, 1 = látka přeložená na půl. */
  folded: number;
  supplier: string;
  ordered_at: number;
  ordered_by: string;
  movements: Movement[];
};

export type Latka = {
  id: string;
  code: string;
  name: string;
  type: string;
  image: string;
  pricePerMeter: number;
};

export const NO_TYPE = 'Bez druhu';
export const SPENT_KEY = '__spent__';
export const SPENT_LABEL = 'Zděláno';
export const SPENT_COLOR = '#fee2e2';

/** Světlé barvy pro vizuální seskupení druhů látek. */
export const PALETTE: { name: string; value: string }[] = [
  { name: 'Bez barvy', value: '' },
  { name: 'Žlutá', value: '#fef3c7' },
  { name: 'Zelená', value: '#dcfce7' },
  { name: 'Modrá', value: '#dbeafe' },
  { name: 'Fialová', value: '#ede9fe' },
  { name: 'Růžová', value: '#fce7f3' },
  { name: 'Oranžová', value: '#ffedd5' },
  { name: 'Tyrkysová', value: '#cffafe' },
  { name: 'Červená', value: '#fee2e2' },
  { name: 'Šedá', value: '#f1f5f9' },
];

export function formatQty(qty: number, unit: string): string {
  const n = Number.isInteger(qty) ? qty.toString() : qty.toFixed(1);
  return `${n} ${unit}`;
}

export function kusLabel(n: number): string {
  if (n === 1) return 'kus';
  if (n >= 2 && n <= 4) return 'kusy';
  return 'kusů';
}

/** Malá písmena bez diakritiky — tolerantní hledání (č→c, ý→y…). */
export function normalize(s: string): string {
  // NFD rozloží „č" na „c" + kombinující háček; ten pak zahodíme.
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/** Text obsahuje všechna slova dotazu (v libovolném pořadí, bez diakritiky)? */
export function matches(haystack: string, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const hay = normalize(haystack);
  return tokens.every((t) => hay.includes(t));
}

export function tokenize(query: string): string[] {
  return query.trim().split(/\s+/).filter(Boolean).map(normalize);
}

export function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

export function parseQty(value: string): number {
  return Number.parseFloat(value.replace(',', '.'));
}

export type TimelineEvent = {
  kind: 'order' | 'receive' | 'subtract' | 'adjust' | 'spent';
  title: string;
  desc: string;
  time: number;
};

function withUser(text: string, user: string): string {
  if (!user) return text;
  return text ? `${text} · ${user}` : user;
}

/** Historie role: objednání → příjem → pohyby. Nejnovější nahoře. */
export function buildTimeline(p: Piece): TimelineEvent[] {
  const events: TimelineEvent[] = [
    {
      kind: 'order',
      title: 'Objednáno',
      desc: withUser(
        p.supplier ? `u dodavatele ${p.supplier}` : formatQty(p.quantity, p.unit),
        p.ordered_by,
      ),
      time: p.ordered_at,
    },
  ];
  if (p.received_at) {
    events.push({
      kind: 'receive',
      title: 'Přijato na sklad',
      desc: withUser(formatQty(p.quantity, p.unit), p.received_by ?? ''),
      time: p.received_at,
    });
  }
  for (const m of p.movements) {
    if (m.type === 'subtract') {
      events.push({
        kind: 'subtract',
        title: `Vyskladněno ${formatQty(Math.abs(m.delta), p.unit)}`,
        desc: withUser(
          // Vyskladnění z detailu zakázky nese její ID — ať je vidět, na co to šlo.
          m.order_ref
            ? `na zakázku ${m.order_ref} · zbývá ${formatQty(m.result_qty, p.unit)}`
            : `zbývá ${formatQty(m.result_qty, p.unit)}`,
          m.user_name,
        ),
        time: m.created_at,
      });
    } else if (m.type === 'spent') {
      events.push({ kind: 'spent', title: 'Zděláno', desc: m.user_name, time: m.created_at });
    } else {
      events.push({
        kind: 'adjust',
        title: `Upraven stav na ${formatQty(m.result_qty, p.unit)}`,
        desc: m.user_name,
        time: m.created_at,
      });
    }
  }
  return events.sort((a, b) => b.time - a.time);
}
